import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import orderBy from 'lodash/orderBy';
import { zezamiiAccess, zezamiiNeutral, zezamiiSemantic } from 'theme/brandTokens';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);

/**
 * Is this booking holding its locker RIGHT NOW — started, not yet ended, not deleted?
 *
 * This is THE definition of "Booked" for the Lockers page, and it is deliberately shared:
 * `getActiveBookedDeviceIDs` paints the tile purple with it, and `getActiveBookingForDevice`
 * decides whether Release Booking is available with it. Two copies of this rule would eventually
 * disagree, and the way it would surface is the worst kind — a locker showing "Booked" whose
 * booking cannot be released, or the reverse.
 */
export const isActiveBooking = (booking, now = new Date()) =>
	Boolean(
		booking?.startTime &&
		booking?.endTime &&
		new Date(booking.startTime) <= now &&
		new Date(booking.endTime) >= now &&
		!booking.deletedDate
	);

// Get the active booked device IDs based on current time and bookings
export const getActiveBookedDeviceIDs = (bookings) => {
	const now = new Date();

	return bookings.filter((b) => isActiveBooking(b, now)).map((b) => b.deviceID);
};

/**
 * The booking to release for a locker — the one currently holding it, or null when it is free.
 *
 * Latest `startTime` wins if a locker somehow holds overlapping active bookings: that is the one
 * that most recently took the locker, so it is the one an operator means by "release this". Not
 * expected, but picking deterministically beats letting array order decide which booking a
 * destructive action lands on.
 */
export const getActiveBookingForDevice = (bookings, deviceID) => {
	if (!Array.isArray(bookings) || deviceID == null) return null;
	const now = new Date();

	return (
		bookings
			.filter((b) => b?.deviceID === deviceID && isActiveBooking(b, now))
			.sort((a, b) => new Date(b.startTime) - new Date(a.startTime))[0] || null
	);
};

// Format a number with leading zero if it's less than 10
// Returns '0' for 0, '01' for 1, '10'
export const formatWithLeadingZero = (number) => {
	if (number === 0) {
		return '0';
	}
	return String(number).padStart(2, '0');
};

// ── Lockers chart palette — single source for every metric-card colour ──────
// Domain chart colours live here (not brandTokens: that file is the brand
// vocabulary; these must stay in lockstep with segmentedOptions below).

// Status colours. segmentedOptions.Availability REFERENCES these, so a status
// can never drift between the grid tiles and the charts. Before this existed,
// #FF6677 meant Booked on Home and Maintenance on Lockers.
// ⚠️ Available was AMBER (#FFC107) and Maintenance a coral (#FF7043): the app's warning
// tone on the HEALTHY state, and a fault that never quite reached the danger tone. A grid
// of free lockers read as a wall of caution. Available is now the success tone and
// Maintenance the danger tone, so the tile colours say what they mean. Both are checked
// against LOCKER_CATEGORY_COLORS below — success #16A34A is distinct from the mint
// #00C9A7, and danger #DC2626 from the orange #F97316 (which is why Maintenance is red
// rather than the warning amber it would otherwise deserve).
export const lockerStatusColors = {
	Booked: '#A85EFF',
	Available: zezamiiSemantic.success,
	Maintenance: zezamiiSemantic.danger
};

// Category series colours for the booked-per-area cards. Both cards map the
// same ordered getLockerMetricsGroupedByCategory output, so indexing this
// shared ramp keeps a category the same colour on Home and Lockers. None of
// these equal a lockerStatusColors value, so on the pages showing both, a
// category accent never reads as a status.
export const LOCKER_CATEGORY_COLORS = [zezamiiAccess.access3, '#00C9A7', '#C084FC', '#4F9DF7', '#F97316', '#2C5282'];

export const getLockerCategoryColor = (index) => LOCKER_CATEGORY_COLORS[index % LOCKER_CATEGORY_COLORS.length];

// People-page card palettes (colocated so a palette change touches one file)
// Inactive was AMBER here and grey on /access/people. Amber is the warning tone, so a
// perfectly benign archived person read as needing attention — the same record, two
// meanings, depending which product you were in. Now grey, matching statusTones.inactive.
// The two products' person VOCABULARIES stay separate on purpose (a Locker "Active" and
// an Access "Active" are not the same claim); it is only the TONE that is shared.
export const lockerPersonStatusColors = {
	Active: zezamiiSemantic.success,
	Inactive: zezamiiNeutral.grey500
};

export const lockerUnlockMethodColors = {
	App: '#FFC107',
	Remote: '#A85EFF',
	Passkey: '#00C9A7',
	Other: '#D9D9D9'
};

// Single-series accents (weekly-activity bars, per-person unlock trend)
export const lockerChartAccents = {
	weeklyActivity: '#FF6677',
	unlockTrend: '#A85EFF'
};

// Usage bands — ONE table drives the band rule (getUsageBand), the grid
// legend below, and through it the LockerDrawer's text. `min` is inclusive.
export const USAGE_BANDS = [
	{ text: 'High', min: 60, description: '60-100 quarterly', color: '#6A0DAD' },
	{ text: 'Medium', min: 40, description: '40-60 quarterly', color: '#A85EFF' },
	{ text: 'Low', min: 10, description: '10-40 quarterly', color: '#E0B3FF' },
	{ text: 'None', min: 0, description: '0-9 quarterly', color: '#D9D9D9' }
];

// Define color options for each segment
export const segmentedOptions = {
	'Availability': [
		{ text: 'Booked', description: 'Lockers currently booked', color: lockerStatusColors.Booked },
		{ text: 'Available', description: 'Lockers available for booking', color: lockerStatusColors.Available }
	],
	'Maintenance': [
		{ text: 'Broken Door', description: 'Locker door needs repair', color: '#A85EFF' },
		{ text: 'Jammed Lockers', description: 'Lockers cannot be opened or closed properly', color: '#FF7043' },
		{ text: 'Battery Issues', description: 'Locker needs battery changing', color: '#00C9A7' },
		{ text: 'Card Reader Fail', description: 'Locker unable to read card', color: '#FFC107' },
		// Any other reported fault, including the seeded "Other" type. Without this
		// an unmatched type fell through to the same grey as "no maintenance", so a
		// locker reported UNUSABLE looked healthy on the board.
		{ text: 'Other Fault', description: 'Reported unusable for another reason', color: '#5B8DEF' },
		// Grey appears on the board for lockers with nothing reported; it belongs in
		// the legend rather than being an unexplained absence of colour.
		{ text: 'None', description: 'No maintenance reported', color: '#D9D9D9' }
	],
	'Campers': [
		{ text: 'Camped', description: 'Booked lockers not accessed in 30+ days', color: '#00C9A7' },
		{ text: 'Active', description: 'Lockers accessed regularly', color: '#D9D9D9' }
	],
	'Locker Usage': USAGE_BANDS.map(({ text, description, color }) => ({ text, description, color })),
	'Sizing': [
		{ text: 'Extra Large', description: 'Size Xl', color: '#FF7043' },
		{ text: 'Large', description: 'Size L', color: '#A85EFF' },
		{ text: 'Medium', description: 'Size M', color: '#00C9A7' },
		{ text: 'Small', description: 'Size S', color: '#FFC107' }
	],
	'Battery': [
		{ text: 'High', description: '75%-100%', color: '#A85EFF' },
		{ text: 'Medium', description: '30%-74%', color: '#00C9A7' },
		{ text: 'Low', description: '15%-29%', color: '#FFC107' },
		{ text: 'Critical', description: '1%-14%', color: '#FF7043' },
		// A flat battery is a fault, so it gets a fault colour. It used to share the
		// neutral grey with "we have no reading", which both hid genuinely dead
		// lockers among unknown ones and made the worst state the calmest on screen.
		{ text: 'Dead', description: '0%', color: '#C1121F' },
		{ text: 'No reading', description: 'No battery reported for this locker', color: '#D9D9D9' }
	]
};

// The lockers log window — the fetch bound and the "quarterly" analytics below
// are the same product fact; one spelling keeps them from disagreeing.
export const LOCKER_LOG_SCOPE = 'all';
export const LOCKER_LOG_DAYS = 90;
//📌 The loadLogsWindow payload, built here because FOUR places dispatch this window
// (hook, app hydration, Loading retry, socket burst) and must not drift apart.
export const lockerLogWindowRequest = (extra) => ({
	scopeKey: LOCKER_LOG_SCOPE,
	days: LOCKER_LOG_DAYS,
	...extra
});
// Derived, not re-spelled: 3 calendar months is 89-92 days, so a second literal
// would let the analytics filter a window the fetch never asked for.
export const lockerLogWindowStart = (now = dayjs()) => now.subtract(LOCKER_LOG_DAYS - 1, 'day').startOf('day');

// Lock Mac custom field. No longer joins logs to devices — the server does that
// and sends deviceID — but device forms and the Control sync still read it.
export const getDeviceLockMac = (device) =>
	device?.fields?.find((f) => f.fieldTitle === 'Lock Mac' && !f.fieldDeletedDate)?.value;

/**
 * Precomputed lookup maps for the Lockers grid. Build ONCE per data change
 * (O(bookings + logs + maintenance + people)) and hand to getTooltipText /
 * getBackgroundColor — the per-tile helpers previously rescanned the full
 * arrays for every device, making the grid O(devices × logs) per render.
 *
 * Split into two builders so the clock-tick consumer (booking maps need `now`;
 * bookings start/expire client-side with no store change) doesn't force the
 * log/maintenance/people pass to rerun every minute.
 */

// Newest entry per key, compared rather than first-seen-wins: nothing pins the client
// to the server's ORDER BY. `tsField` is the caller's date column.
const keepLatest = (map, key, entry, tsField) => {
	const current = map.get(key);
	if (!current || new Date(entry[tsField]) > new Date(current[tsField])) {
		map.set(key, entry);
	}
};

export const buildLockerBookingMaps = ({ bookings = [], now = new Date() } = {}) => {
	// Active-now booking per device (latest startTime wins — same rule as
	// getActiveBookingForDevice) + the earliest upcoming booking per device
	const activeBookingByDeviceID = new Map();
	const upcomingBookingByDeviceID = new Map();
	bookings.forEach((booking) => {
		if (isActiveBooking(booking, now)) {
			const current = activeBookingByDeviceID.get(booking.deviceID);
			if (!current || new Date(booking.startTime) > new Date(current.startTime)) {
				activeBookingByDeviceID.set(booking.deviceID, booking);
			}
		} else if (!booking.deletedDate && new Date(booking.startTime) > now) {
			const current = upcomingBookingByDeviceID.get(booking.deviceID);
			if (!current || new Date(booking.startTime) < new Date(current.startTime)) {
				upcomingBookingByDeviceID.set(booking.deviceID, booking);
			}
		}
	});

	return { activeBookingByDeviceID, upcomingBookingByDeviceID };
};

export const buildLockerDataMaps = ({ logs = [], maintenance = [], people = [], maintenanceTypes = [] } = {}) => {
	const personNameByID = new Map(people.map((p) => [p.personID, p.displayName]));

	// Keyed on deviceID, which the server already resolved from the lock's MAC.
	// The MAC could not: 306 of 856 lockers carry two `Lock Mac` rows, 2 disagree.
	const usageByDeviceID = new Map();
	const latestLogByDeviceID = new Map();
	logs.forEach((log) => {
		if (!log.deviceID) return;
		usageByDeviceID.set(log.deviceID, (usageByDeviceID.get(log.deviceID) || 0) + 1);
		keepLatest(latestLogByDeviceID, log.deviceID, log, 'eventAt');
	});

	// Latest log per asset (tooltip: latest report) + latest ACTIVE log per
	// asset (colour: unresolved, undeleted)
	const maintenanceByAssetID = new Map();
	const activeMaintenanceByAssetID = new Map();
	maintenance.forEach((log) => {
		keepLatest(maintenanceByAssetID, log.assetID, log, 'createdDate');
		if (!log.resolvedDate && !log.deletedDate) {
			keepLatest(activeMaintenanceByAssetID, log.assetID, log, 'createdDate');
		}
	});

	const maintenanceTypeByID = new Map(maintenanceTypes.map((t) => [t.maintenanceTypeID, t]));

	return {
		personNameByID,
		usageByDeviceID,
		latestLogByDeviceID,
		maintenanceByAssetID,
		activeMaintenanceByAssetID,
		maintenanceTypeByID
	};
};

export const buildLockerGridContext = ({
	bookings = [],
	logs = [],
	maintenance = [],
	people = [],
	maintenanceTypes = [],
	now = new Date()
} = {}) => ({
	...buildLockerBookingMaps({ bookings, now }),
	...buildLockerDataMaps({ logs, maintenance, people, maintenanceTypes })
});

//Determine tooltip text based on selected segment and device data
export const getTooltipText = ({ selectedSegment, device, context }) => {
	switch (selectedSegment) {
		case 'Availability':
			return getAvailabilityTooltip(device, context);

		case 'Maintenance':
			return getMaintenanceTooltip(device, context);

		case 'Campers':
			return getCampersTooltip(device, context);

		case 'Locker Usage':
			return getLockerUsageTooltip(device, context);

		case 'Sizing':
			return getSizingTooltip(device);

		case 'Battery':
			return getBatteryTooltip(device);

		default:
			return device.name;
	}
};

// Get the availability tooltip text for a device. Only an ACTIVE booking gets
// the occupant treatment — the tile colour uses the same rule, so the tooltip
// can no longer name an occupant on a tile painted Available (which happened
// when this scanned for ANY non-expired booking, including future ones).
const getAvailabilityTooltip = (device, context) => {
	const booking = context.activeBookingByDeviceID.get(device.deviceID);
	if (!booking) {
		const upcoming = context.upcomingBookingByDeviceID.get(device.deviceID);
		if (upcoming) {
			return `Available — next booking starts ${dayjs(upcoming.startTime).format('D MMMM')}`;
		}
		return 'Locker is available';
	}

	const person = context.personNameByID.get(booking.assignedToPerson) || '';
	const start = dayjs(booking.startTime).format('D MMMM');
	const end = dayjs(booking.endTime).format('D MMMM');
	const startHour = dayjs(booking.startTime).format('HH:mm A');
	const endHour = dayjs(booking.endTime).format('HH:mm A');

	return (
		<>
			{person}
			<br />
			{start} - {end}
			<br />
			{startHour} - {endHour}
		</>
	);
};

// Get the maintenance tooltip text for a device
const getMaintenanceTooltip = (device, context) => {
	const mLog = context.maintenanceByAssetID.get(device.deviceID);

	if (!mLog) return 'No maintenance reported';

	const type = context.maintenanceTypeByID.get(mLog.maintenanceTypeID);
	// Same field the tile colour matches on, so the two cannot disagree. Prod has
	// a type whose `name` is "test123" while its userFriendlyName is "Broken Door";
	// reading `name` here put that string in front of a customer.
	const label = type?.userFriendlyName || type?.name || 'Unknown';
	const reported = dayjs(mLog.createdDate).format('MMMM D, YYYY');

	// The tile is coloured from the latest ACTIVE log, the tooltip from the latest
	// log of any status. When the newest one is resolved the tile is grey, so the
	// tooltip has to say so — otherwise it names a fault on a locker shown healthy.
	const isResolved = Boolean(mLog.resolvedDate);
	if (isResolved) {
		return `${label} - reported ${reported}, resolved ${dayjs(mLog.resolvedDate).format('MMMM D, YYYY')}`;
	}

	return `${label} - Reported ${reported}`;
};

// Get the campers tooltip text for a device. Says whether the locker is BOOKED
// as well as when it was last used — the tile colour now depends on both, so a
// tooltip that mentioned only access would contradict the tile it explains.
const getCampersTooltip = (device, context) => {
	const isBooked = context.activeBookingByDeviceID.has(device.deviceID);
	const recentLog = context.latestLogByDeviceID.get(device.deviceID);

	const lastUse = () => {
		if (!recentLog) return 'never accessed';
		const daysSince = dayjs().diff(dayjs(recentLog.eventAt), 'day');
		if (daysSince === 0) return 'last accessed today';
		if (daysSince === 1) return 'last accessed yesterday';
		return `last accessed ${daysSince} days ago`;
	};

	// Not booked: nobody is holding it, so camping does not apply however long it
	// has been idle. Say so, rather than implying neglect.
	if (!isBooked) return `Not booked — ${lastUse()}`;

	return isCamping(device, context) ? `Camped — booked, ${lastUse()}` : `Booked and in use — ${lastUse()}`;
};

// Get the locker usage tooltip text for a device
const getLockerUsageTooltip = (device, context) => {
	const usageCount = context.usageByDeviceID.get(device.deviceID) || 0;
	return `Unlocked ${usageCount} ${usageCount === 1 ? 'time' : 'times'} over the last 3 months`;
};

// Get the locker usage count for a specific device
export const getLockerUsageCount = (device, logs) => logs.filter((l) => l.deviceID === device.deviceID).length;

// One banding rule for usage, driven by USAGE_BANDS — shared by the grid
// colour, legend and the LockerDrawer's text. These previously disagreed (the
// drawer called ≥40 'High' while the legend said High starts at 60), so the
// drawer could contradict the tile it opened from.
export const getUsageBand = (usageCount) => USAGE_BANDS.find((band) => usageCount >= band.min).text;

// Get the locker usage category (High, Medium, Low, None) for a specific device
export const getLockerUsageCategory = (device, logs) => getUsageBand(getLockerUsageCount(device, logs));

/**
 * The device's Size field value, trimmed, ignoring deleted fields.
 *
 * ONE reader for the size, because the three consumers previously disagreed:
 * the tooltip did not filter `fieldDeletedDate` while the colour and category
 * did, so a deleted Size still showed "Size: Large" on an unsized grey tile.
 *
 * Trimming matters: prod holds a value of `" Medium"` with a leading space,
 * which failed the exact `===` match and greyed the tile while the drawer still
 * reported " Medium".
 */
export const getDeviceSizeValue = (device) =>
	device?.fields?.find((field) => field.fieldTitle === 'Size' && !field.fieldDeletedDate)?.value?.trim() || '';

// Get the size category for a specific device
export const getDeviceSizeCategory = (device) => {
	const sizeValue = getDeviceSizeValue(device);

	if (!sizeValue) return '-';

	const sizingOptions = segmentedOptions['Sizing'];
	const foundOption = sizingOptions.find((opt) => opt.text === sizeValue);

	return foundOption?.text || sizeValue;
};

// Get the sizing tooltip text for a device
const getSizingTooltip = (device) => {
	const sizeValue = getDeviceSizeValue(device);
	return sizeValue ? `Size: ${sizeValue}` : 'Size not specified';
};

/**
 * When the current battery reading was actually taken.
 *
 * Reads 'Battery Last Read' — a custom field the App Server stamps ONLY when a
 * probe succeeds (ConnectivityStateWriter), not on every sweep. That distinction
 * is the point: 'Connectivity Last Checked' moves every run regardless of
 * outcome, so it can't tell a fresh reading from a stale one surviving a run of
 * failed probes. Every reading in prod was 6-24 months old before that writer
 * covered lockers at all — this is what lets the hover keep saying so once a
 * reading exists, instead of only showing a number with no age attached.
 */
export const getBatteryLastReadAt = (device) => {
	const raw = device?.fields?.find((f) => f.fieldTitle === 'Battery Last Read' && !f.fieldDeletedDate)?.value;
	if (!raw) return null;
	const parsed = dayjs(raw);
	return parsed.isValid() ? parsed : null;
};

// Get the battery tooltip text for a device. Uses the same reader as the tile so
// the two agree; `isNaN(null)` is false, so the old check rendered the literal
// string "Battery: null%" for a locker with no reading.
const getBatteryTooltip = (device) => {
	const level = getBatteryLevel(device);
	if (level === null) return 'No battery reading for this locker';

	const lastRead = getBatteryLastReadAt(device);
	return lastRead ? `Battery: ${level}% — checked ${lastRead.fromNow()}` : `Battery: ${level}%`;
};

// Determine background color based on device status and selected segment
export const getBackgroundColor = ({ device, selectedSegment, context }) => {
	switch (selectedSegment) {
		case 'Availability':
			return getAvailabilityColor(device, context);

		case 'Maintenance':
			return getMaintenanceColor(device, context);

		case 'Campers':
			return getCampersColor(device, context);

		case 'Locker Usage':
			return getLockerUsageColor(device, context);

		case 'Sizing':
			return getSizingColor(device);

		case 'Battery':
			return getBatteryColor(device);

		default:
			return '#D9D9D9';
	}
};

// Get the color for the device based on its availability status
export const getAvailabilityColor = (device, context) => {
	const isBooked = context.activeBookingByDeviceID.has(device.deviceID);

	return isBooked ? lockerStatusColors.Booked : lockerStatusColors.Available;
};

// Get the color for the device based on its maintenance status
/**
 * Map a maintenance type record to its legend entry.
 *
 * Matching is on `userFriendlyName` — the customer-facing label the legend is
 * written from — falling back to `name`. Prod holds types whose two fields
 * disagree (id 12 is name "test123" / userFriendlyName "Broken Door"), so
 * picking one field and stating which keeps the tile and the tooltip agreeing.
 *
 * An unrecognised type resolves to "Other Fault", never to "None". The seeded
 * "Other" type is classified Unusable, and previously matched none of the four
 * substrings and fell through to the same grey as no-maintenance-at-all — a
 * locker reported unusable read as healthy.
 */
export const getMaintenanceLegendText = (typeRecord) => {
	const label = (typeRecord?.userFriendlyName || typeRecord?.name || '').toLowerCase();

	if (label.includes('broken')) return 'Broken Door';
	if (label.includes('jammed')) return 'Jammed Lockers';
	if (label.includes('battery')) return 'Battery Issues';
	if (label.includes('card reader')) return 'Card Reader Fail';
	return 'Other Fault';
};

export const getMaintenanceColor = (device, context) => {
	const maintenanceOptions = segmentedOptions['Maintenance'];
	const activeLog = context.activeMaintenanceByAssetID.get(device.deviceID);

	if (!activeLog) {
		return maintenanceOptions.find((opt) => opt.text === 'None')?.color;
	}

	const typeRecord = context.maintenanceTypeByID.get(activeLog.maintenanceTypeID);
	const legendText = getMaintenanceLegendText(typeRecord);

	return maintenanceOptions.find((opt) => opt.text === legendText)?.color;
};

// Days without access after which a BOOKED locker counts as camped. One
// definition, shared by the tile colour and the tooltip below — they used to
// hardcode 30 separately.
export const CAMPING_THRESHOLD_DAYS = 30;

/**
 * Is this locker being camped — held by a booking whose owner has not touched
 * it in CAMPING_THRESHOLD_DAYS?
 *
 * Camping is about someone HOLDING a locker they do not use, which is why the
 * booking is half the test. This previously looked only at last access, so every
 * idle unbooked locker — an empty locker doing nothing wrong — was painted
 * Camped alongside the real hoarders, and the segment over-reported by the whole
 * free-locker population.
 */
export const isCamping = (device, context) => {
	// No active booking → nobody is holding it → not camping, however long it has
	// sat untouched.
	if (!context.activeBookingByDeviceID.has(device.deviceID)) return false;

	const cutoff = dayjs().subtract(CAMPING_THRESHOLD_DAYS, 'day');
	const latestLog = context.latestLogByDeviceID.get(device.deviceID);

	// Booked and never accessed in the log window = held and unused.
	if (!latestLog) return true;

	return dayjs(latestLog.eventAt).isBefore(cutoff);
};

// Get the color for the device based on its camping status
export const getCampersColor = (device, context) => {
	const camperOptions = segmentedOptions['Campers'];
	const wanted = isCamping(device, context) ? 'Camped' : 'Active';

	return camperOptions.find((opt) => opt.text === wanted)?.color;
};

// Get the color for the device based on its locker usage
export const getLockerUsageColor = (device, context) => {
	const usageCount = context.usageByDeviceID.get(device.deviceID) || 0;

	return USAGE_BANDS.find((band) => usageCount >= band.min).color;
};

// Get the color for the device based on its sizing
export const getSizingColor = (device) => {
	const sizeValue = getDeviceSizeValue(device);
	const sizingOptions = segmentedOptions['Sizing'];

	return (
		sizingOptions.find((opt) => opt.text === sizeValue)?.color || '#D9D9D9' // unsized / unrecognised
	);
};

/**
 * The device's battery percentage, or null when there is no usable reading.
 *
 * `device.battery` is derived server-side from the "Electric Quantity" custom
 * field and is null when the field is absent or unparseable. Distinguishing that
 * from 0 matters: a locker nobody has a reading for is not a locker with a flat
 * battery, and conflating them both hides real dead lockers and sends someone to
 * swap a healthy one.
 */
export const getBatteryLevel = (device) => {
	const value = device?.battery;
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

/** Legend bucket for a battery level. `null` (no reading) is its own bucket. */
export const getBatteryBand = (level) => {
	if (level === null) return 'No reading';
	if (level <= 0) return 'Dead';
	if (level < 15) return 'Critical';
	if (level < 30) return 'Low';
	if (level < 75) return 'Medium';
	return 'High';
};

// Get the color for the device based on its battery status
export const getBatteryColor = (device) => {
	const batteryOptions = segmentedOptions['Battery'];
	const band = getBatteryBand(getBatteryLevel(device));

	return batteryOptions.find((opt) => opt.text === band)?.color;
};

// Get weekly activity data for the last 7 days (Saturday to Friday)
export const getWeeklyActivityData = (logs = []) => {
	const dayCounts = new Array(7).fill(0); // Saturday to Friday

	const today = dayjs().startOf('day');
	const startOfWeek = dayjs().startOf('week').subtract(1, 'day').startOf('day'); // Saturday

	logs.forEach((log) => {
		const logDate = dayjs(log.eventAt).startOf('day');

		if (logDate.isSameOrAfter(startOfWeek) && logDate.isSameOrBefore(today)) {
			const dayIndex = (logDate.day() + 1) % 7; // Adjust: Saturday = 6
			dayCounts[dayIndex]++;
		}
	});

	const shortDayNames = ['S', 'S', 'M', 'T', 'W', 'T', 'F'];
	const fullDayNames = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

	const dayActivityData = dayCounts.map((count, index) => ({
		name: shortDayNames[index],
		fullName: fullDayNames[index],
		value: count
	}));

	const maxDayIndex = dayCounts.indexOf(Math.max(...dayCounts));
	const maxActivityDay = fullDayNames[maxDayIndex];

	return { maxActivityDay, dayActivityData };
};

// Get locker utilization data based on logs and selected time frame (weekly, monthly, or quarterly)
export const getLockerUtilizationData = (logs = [], timeFrame = '1') => {
	const now = dayjs();
	const threeMonthsAgo = lockerLogWindowStart(now);

	// Filter logs to only those in the last 6 months if needed for quarterly comparison
	const sixMonthsAgo = now.subtract(6, 'month').startOf('day');
	const filteredLogs =
		timeFrame === '3'
			? logs.filter((log) => dayjs(log.eventAt).isSameOrAfter(sixMonthsAgo))
			: logs.filter((log) => dayjs(log.eventAt).isSameOrAfter(threeMonthsAgo));

	// Build weekly
	if (timeFrame === '1') {
		const weeks = [];

		for (let i = 0; i < 13; i++) {
			const start = dayjs()
				.subtract(12 - i, 'week')
				.startOf('week');
			const end = start.endOf('week');

			const weekLogs = filteredLogs.filter((log) => {
				const logDate = dayjs(log.eventAt);
				return logDate.isSameOrAfter(start) && logDate.isSameOrBefore(end);
			});

			weeks.push({
				name: `W${i + 1}`,
				value: weekLogs.length
			});
		}

		return weeks;
	}

	// Build monthly
	if (timeFrame === '2') {
		const months = [];

		for (let i = 0; i < 3; i++) {
			const start = now.subtract(2 - i, 'month').startOf('month');
			const end = start.endOf('month');

			const monthLogs = filteredLogs.filter((log) => {
				const logDate = dayjs(log.eventAt);
				return logDate.isSameOrAfter(start) && logDate.isSameOrBefore(end);
			});

			months.push({
				name: start.format('MMM'), // 'Jul', 'Aug', etc.
				value: monthLogs.length
			});
		}

		return months;
	}

	// Build quarterly: compare current 3 months vs previous 3 months
	if (timeFrame === '3') {
		const currentTrimesterStart = lockerLogWindowStart(now);
		const previousTrimesterStart = now.subtract(6, 'month').startOf('day');
		const previousTrimesterEnd = currentTrimesterStart.subtract(1, 'day');

		const currentLogs = filteredLogs.filter((log) => dayjs(log.eventAt).isSameOrAfter(currentTrimesterStart));

		const previousLogs = filteredLogs.filter((log) => {
			const logDate = dayjs(log.eventAt);
			return logDate.isSameOrAfter(previousTrimesterStart) && logDate.isSameOrBefore(previousTrimesterEnd);
		});

		return [
			{ name: 'Prev Trimester', value: previousLogs.length },
			{ name: 'Current Trimester', value: currentLogs.length }
		];
	}

	return [];
};

// Calculate the percentage increase in usage between two time frames (weekly, monthly, or quarterly)
export const getUsageIncreasePercentage = (logs = [], timeFrame = '1') => {
	const now = dayjs();

	let currentStart, currentEnd, previousStart, previousEnd;

	if (timeFrame === '1') {
		// Weekly
		currentStart = now.startOf('week');
		currentEnd = now.endOf('week');
		previousStart = currentStart.subtract(1, 'week');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	} else if (timeFrame === '2') {
		// Monthly
		currentStart = now.startOf('month');
		currentEnd = now.endOf('month');
		previousStart = currentStart.subtract(1, 'month');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	} else if (timeFrame === '3') {
		// Trimester (3 months)
		currentStart = lockerLogWindowStart(now);
		currentEnd = now.endOf('day');
		previousStart = now.subtract(6, 'month').startOf('day');
		previousEnd = currentStart.subtract(1, 'day').endOf('day');
	}

	const currentLogs = logs.filter((log) => {
		const date = dayjs(log.eventAt);
		return date.isSameOrAfter(currentStart) && date.isSameOrBefore(currentEnd);
	});

	const previousLogs = logs.filter((log) => {
		const date = dayjs(log.eventAt);
		return date.isSameOrAfter(previousStart) && date.isSameOrBefore(previousEnd);
	});

	const currentCount = currentLogs.length;
	const previousCount = previousLogs.length;

	if (previousCount === 0 && currentCount === 0) return 0;
	if (previousCount === 0 && currentCount > 0) return 100;

	const increase = ((currentCount - previousCount) / previousCount) * 100;

	return Math.min(Number(increase.toFixed(0)), 100);
};

// Get locker metrics grouped by category(booked lockers and available lockers for each category)
export const getLockerMetricsGroupedByCategory = ({ devices, bookings, categories }) => {
	const now = new Date();

	const activeBookedDeviceIDs = bookings
		.filter(
			(b) =>
				b.startTime && b.endTime && new Date(b.startTime) <= now && new Date(b.endTime) >= now && !b.deletedDate
		)
		.map((b) => b.deviceID);

	return categories.map((category) => {
		const categoryID = category.categoryID;

		const categoryDevices = devices.filter((d) => d.categoryID === categoryID);
		const bookedCount = categoryDevices.filter((d) => activeBookedDeviceIDs.includes(d.deviceID)).length;
		const availableCount = categoryDevices.length - bookedCount;

		return {
			categoryID,
			categoryName: category.name,
			booked: bookedCount,
			available: availableCount,
			total: categoryDevices.length
		};
	});
};

/**
 * Booked / available / maintenance counts for a set of lockers.
 *
 * 🛑 `availableCount` is the count of lockers in NEITHER state, never `total − booked −
 * maintenance`. The subtraction went NEGATIVE — Home's "Available" tile showed **-1** — because a
 * locker that is booked AND has an open fault is a member of both sets and was taken off the total
 * twice. Counting the remainder directly cannot underflow, whatever the overlap.
 *
 * ⚠️ Consequence of that same overlap: the three counts do NOT necessarily sum to `devices.length`.
 * A locker reported broken mid-booking is honestly both, and `bookedCount` feeds utilisation
 * (useSiteRollups, CardMetricSpaceUtilisation) where excluding it would understate how much of the
 * estate is let. The status donut may therefore total slightly more than the locker count; that is
 * preferable to a negative slice, and to quietly redefining "booked".
 *
 * A maintenance log counts only while it is OPEN — `!resolvedDate && !deletedDate`, the same rule
 * `buildLockerDataMaps` colours the locker board by. Counting every log ever filed had the band and
 * the board disagreeing about the same locker, and pushed `available` down for faults already fixed.
 */
export const getLockerMetrics = ({ devices = [], bookings = [], maintenanceLogs = [] }) => {
	if (!devices.length) {
		return {
			bookedCount: 0,
			availableCount: 0,
			maintenanceCount: 0
		};
	}

	const bookedDeviceIDs = new Set(getActiveBookedDeviceIDs(bookings));
	const maintenanceDeviceIDs = new Set(
		maintenanceLogs.filter((log) => !log.resolvedDate && !log.deletedDate).map((log) => log.assetID)
	);

	const bookedCount = devices.filter((d) => bookedDeviceIDs.has(d.deviceID)).length;
	const maintenanceCount = devices.filter((d) => maintenanceDeviceIDs.has(d.deviceID)).length;
	const availableCount = devices.filter(
		(d) => !bookedDeviceIDs.has(d.deviceID) && !maintenanceDeviceIDs.has(d.deviceID)
	).length;

	return {
		bookedCount,
		maintenanceCount,
		availableCount
	};
};

// Sort lockers naturally by name, handling alphanumeric and special characters
export const sortLockersNaturalWay = (items) => {
	if (!items || items.length === 0) {
		return [];
	}
	try {
		return orderBy(
			items,
			[
				// Extract and sort alphabetic segments, if present
				(item) => {
					const alphaMatch = item?.name?.match(/[A-Za-z]+/);
					return alphaMatch ? alphaMatch[0].trim().toUpperCase() : ''; // Normalize and handle missing alphabetic part
				},
				// Extract and sort numeric segments, if present
				(item) => {
					const numericMatch = item?.name?.match(/\d+/);
					return numericMatch ? parseInt(numericMatch[0], 10) : Number.MAX_SAFE_INTEGER; // Handle missing numeric part
				},
				// Handle special characters like hyphens, spaces or dots, to give preference to similar names with separators
				(item) => {
					const specialMatch = item?.name?.match(/[-. ]/);
					return specialMatch ? specialMatch[0] : ''; // Sort based on special characters, if present
				}
			],
			['asc', 'asc', 'asc']
		);
	} catch (error) {
		return items; // Return unsorted items in case of error
	}
};
